import { yupResolver } from '@hookform/resolvers/yup';
import { Cancel, NavigateNext } from '@mui/icons-material';
import { Collapse, Grid, Stack, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  addTeamMembers,
  getInvitedUsersInWorkspace,
  IAddTeamMembersPayload,
  IInviteStatus,
  ITeamFilters,
  IWorkspace,
  searchInvitesUsingEmail,
} from 'wisestep-api';
import {
  SearchBar,
  WsButton,
  WsColors,
  WsDataTable,
  WsDrawer,
  WsPagination,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import InviteUserButton from '../../../features/users/components/InviteUsersButton';
import { useTotalInvitesCall } from '../../../features/users/helper/hooks';
import { invitesSearchDefaultValues, teamFilters } from '../helper/defaultValues';
import { invitesSearchSchema } from '../helper/validation';

import { StatusTypography } from './TeamMembersTable';

const StyledFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 0,
  right: 0,
  justifyContent: 'flex-end',
  width: '50.8vw',
  backgroundColor: WsColors.White,
  borderTop: '1px solid' + WsColors.BorderGrey,
}));

export interface IAddMembersProps {
  teamName: string;
  teamId: string;
  open: boolean;
  onSuccess: () => void;
  handleClose: () => void;
}

const AddMembersDrawer = ({ teamName, teamId, open, onSuccess, handleClose }: IAddMembersProps) => {
  const [invitedUsersInWorkspace, setInvitedUsersInWorkspace] = useState<IWorkspace[]>([]);

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  const [userNotAvailable, setUserNotAvailable] = useState<boolean>(false);

  const [unavailableUser, setUnavailableUser] = useState<string>('');

  const [maxInvites] = useTotalInvitesCall();

  const [currentPage, setCurrentPage] = useState(1);

  const [pendingUsersLimit, setPendingUsersLimit] = useState<number>(maxInvites);

  const [query, setQuery] = useState<ITeamFilters>(teamFilters);

  const [totalTeamMembers, setTotalTeamMembers] = useState(0);

  const methods = useForm({
    resolver: yupResolver(invitesSearchSchema),
    defaultValues: invitesSearchDefaultValues,
  });

  const invitedUserEmails: string[] = [];
  const { reset } = methods;

  const columns = useMemo(
    () => [
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Role',
        accessor: 'role',
      },
      {
        Header: 'Invite Status',
        accessor: 'status',
        Cell: (cell: Cell) => <StatusTypography cell={cell}>{cell.value}</StatusTypography>,
      },
    ],
    []
  );

  const data = useMemo(
    () =>
      invitedUsersInWorkspace?.map((user) => ({
        email: user?.userId,
        role: user.role,
        status: user.barred
          ? IInviteStatus.DEACTIVATED
          : user.accepted
          ? IInviteStatus.ACTIVATED
          : IInviteStatus.INVITED,
      })),
    [invitedUsersInWorkspace]
  );

  const getSelectedRows = (rows: number[]) => {
    setSelectedUsers(rows);
  };

  interface IInviteSearchData {
    invitesSearch: string;
  }

  const handleSearchInvites = async (data: IInviteSearchData) => {
    if (data.invitesSearch !== '') {
      const response = await searchInvitesUsingEmail(data.invitesSearch);
      const array = [response.data];
      if (response.data) {
        setInvitedUsersInWorkspace(array);
        setTotalTeamMembers(array.length);
      } else {
        setUnavailableUser(data.invitesSearch);
        setUserNotAvailable(true);
      }
    } else {
      setUserNotAvailable(false);
      getUsersInWorkspace();
    }
  };

  const handleCloseAndReset = () => {
    handleClose();
    reset();
    handleSearchInvites({ invitesSearch: '' });
  };

  const handleAddMembers = async () => {
    const addTeamPayload: IAddTeamMembersPayload = {
      teamMemberDTOList: selectedUsers.map((index) => ({
        email: invitedUsersInWorkspace[index].userId,
        role: invitedUsersInWorkspace[index].role,
        roles: ['ROLE_USER', 'ROLE_DOWNLOAD'],
        teamId: teamId,
      })),
    };
    await addTeamMembers(addTeamPayload);
    handleClose();
    onSuccess();
  };
  const getUsersInWorkspace = useCallback(async () => {
    const response = await getInvitedUsersInWorkspace(query);
    const filteredData = response.data.content;
    setTotalTeamMembers(response.data.totalElements);
    setInvitedUsersInWorkspace(filteredData);
    const activeLength = filteredData.length;
    setPendingUsersLimit(maxInvites - activeLength);
  }, [maxInvites, query]);

  useEffect(() => {
    getUsersInWorkspace();
  }, [getUsersInWorkspace, query]);

  invitedUsersInWorkspace.map((user) => user.userId && invitedUserEmails.push(user.userId));

  return (
    <WsDrawer title={'Add members to ' + teamName} open={open} onClose={handleCloseAndReset}>
      <FormProvider {...methods}>
        <Stack
          spacing={2}
          direction="row"
          alignItems={'center'}
          p={2}
          component="form"
          onSubmit={methods.handleSubmit(handleSearchInvites)}
        >
          <SearchBar name="invitesSearch" placeholder="Search for invited users" />
          <WsButton type="submit">Search</WsButton>
        </Stack>
        <Collapse in={userNotAvailable}>
          <Stack direction={'row'} alignItems={'center'} spacing={2} p={2}>
            <WsTypography
              variant={WsTypoVariants.BODY1}
              color={WsTypoColors.ERROR}
            >{`Invite ${unavailableUser} to workspace ?`}</WsTypography>
            <InviteUserButton
              defaultInvites={[unavailableUser]}
              maxInvites={maxInvites}
              pendingUsersLimit={pendingUsersLimit}
            />
          </Stack>
        </Collapse>
      </FormProvider>
      <WsPagination
        count={Math.ceil(totalTeamMembers / query.pageSize)}
        onChange={(_, page) => {
          setCurrentPage(page + 1);
          setQuery({ ...query, pageNumber: page + 1 });
        }}
        itemsPerPage={query.pageSize}
        pageNumber={currentPage - 1}
        defaultPage={currentPage}
        isPageCount={true}
        type="Teams"
        totalCount={totalTeamMembers}
      />
      <WsDataTable
        columns={columns}
        data={data}
        selectable={true}
        getSelectedRows={getSelectedRows}
        boldItems={['email']}
      />
      <StyledFooter>
        <Stack direction={'row'} spacing={2} justifyContent={'flex-end'} p={2}>
          <WsButton size="large" variant="outlined" endIcon={<Cancel />} onClick={handleClose}>
            Cancel
          </WsButton>
          <WsButton size="large" endIcon={<NavigateNext />} onClick={() => handleAddMembers()}>
            Add Members
          </WsButton>
        </Stack>
      </StyledFooter>
    </WsDrawer>
  );
};

export default AddMembersDrawer;
